import {
  TimelineScrollAnchor,
  TimelineScrollRequest,
  TimelineTimeframes,
  Timestamp,
} from '@griply/types';
import {
  getRoadmapReferenceDate,
  incrementRoadmapReferenceDate,
} from '@griply/utils';
import { useCallback, useEffect, useState } from 'react';

export type UseRoadmapNavigationOptions = {
  timeframe: TimelineTimeframes;
  today: Timestamp;
};

// referenceDate is always a timeframe start (what prev/next step through); scrollRequest picks the edge/center anchor.
export const useRoadmapNavigation = ({
  timeframe,
  today,
}: UseRoadmapNavigationOptions) => {
  const [referenceDate, setReferenceDate] = useState<Timestamp>(() =>
    getRoadmapReferenceDate(timeframe, today),
  );
  const [scrollRequest, setScrollRequest] = useState<TimelineScrollRequest>({
    anchor: TimelineScrollAnchor.Today,
    id: 0,
  });

  const requestScroll = useCallback(
    (anchor: TimelineScrollAnchor) =>
      setScrollRequest(({ id }) => ({ anchor, id: id + 1 })),
    [],
  );

  // Explicit timeframe: a switch centers before the persisted timeframe echoes back through props.
  const centerTodayIn = useCallback(
    (forTimeframe: TimelineTimeframes) => {
      setReferenceDate(getRoadmapReferenceDate(forTimeframe, today));
      requestScroll(TimelineScrollAnchor.Today);
    },
    [today, requestScroll],
  );

  const onToday = useCallback(
    () => centerTodayIn(timeframe),
    [centerTodayIn, timeframe],
  );

  const onNext = useCallback(() => {
    setReferenceDate((current) =>
      incrementRoadmapReferenceDate(timeframe, current, 1),
    );
    requestScroll(TimelineScrollAnchor.TimeframeStart);
  }, [timeframe, requestScroll]);

  const onPrev = useCallback(() => {
    setReferenceDate((current) =>
      incrementRoadmapReferenceDate(timeframe, current, -1),
    );
    requestScroll(TimelineScrollAnchor.TimeframeStart);
  }, [timeframe, requestScroll]);

  useEffect(() => {
    setReferenceDate((current) => {
      const snapped = getRoadmapReferenceDate(timeframe, current);
      return snapped.getTime() === current.getTime() ? current : snapped;
    });
  }, [timeframe]);

  return {
    referenceDate,
    scrollRequest,
    centerTodayIn,
    onToday,
    onNext,
    onPrev,
  };
};
